import {
  computed,
  Signal,
  signal,
  WritableSignal,
  inject,
  Service,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  AuthError,
  AuthOtpResponse,
  createClient,
  OAuthResponse,
  PostgrestSingleResponse,
  Provider,
  Session,
  SupabaseClient,
} from "@supabase/supabase-js";
import { ExchangeStravaTokenResponse } from "../../../supabase/functions/_shared/_interfaces/exchange-strava-token-response";

import { Database } from "../../../supabase/functions/_shared/_types/database";

import { toObservable } from "@angular/core/rxjs-interop";
import { filter, map, Observable, take } from "rxjs";
import { environment } from "../../environments/environment";
import {
  Frequency,
  LeaderboardResponse,
  StravaActivity,
  StravaAthlete,
  Streak,
  StreakEvent,
  StreakEventToCreate,
  StreakToCreate,
  StreakUpdate,
  StreakWithActivities,
  UserProfile,
  UserProfileUpdate,
} from "../types/supabase-types";
import { DateHelperService } from "./date-helper.service";
import { StreakService } from "./streak.service";

@Service()
export class SupabaseService {
  private readonly router = inject(Router);
  private readonly dateHelperService = inject(DateHelperService);
  private readonly streakService = inject(StreakService);
  private readonly client: SupabaseClient;

  /**
   * Session types:
   * Session = logged in,
   * null = logged out,
   * undefined = unknown
   */
  private readonly _session: WritableSignal<Session | null | undefined> =
    signal(undefined);

  /**
   * Observable that resolves to the user's login status.
   *
   * This observable:
   * 1. Waits for the session to be initialized (not undefined)
   * 2. Maps the session to a boolean login status
   * 3. Emits only once and then completes
   *
   * Note: Supabase is initialized on app start, which sets the initial session state.
   * This ensures we have a definitive logged-in status before emitting.
   */
  public readonly isLoggedIn: Observable<boolean> = toObservable(this.session)
    .pipe(
      filter((session) => session !== undefined),
      map((session) => session !== null),
      take(1),
    );

  constructor() {
    this.client = createClient<Database>(
      environment.SUPABASE_URL,
      environment.SUPABASE_KEY,
    );
  }

  public init(): void {
    this.listenToAuthChanges();
  }

  public get session(): Signal<Session | null | undefined> {
    return computed(() => this._session());
  }

  private listenToAuthChanges() {
    this.client.auth.onAuthStateChange(
      (event, session) => {
        this._session.set(session);

        if (event === "SIGNED_OUT") {
          this.onSignOut();
        }
      },
    );
  }

  private onSignOut(): void {
    this.router.navigate(["/"]);
  }

  public async signIn(email: string): Promise<AuthOtpResponse> {
    const response = await this.client.auth.signInWithOtp({
      email: email,
      options: {
        emailRedirectTo: window.origin + "/dashboard",
      },
    });

    if (response.error) {
      // TODO: handle error
      console.error(response.error);
      throw (response.error);
    }

    return response;
  }

  public async oAuthLogin(provider: Provider): Promise<OAuthResponse> {
    const response = await this.client.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: window.origin + "/dashboard",
      },
    });

    if (response.error) {
      // TODO: handle error
      console.error(response.error);
      throw (response.error);
    }

    return response;
  }

  public async signOut(): Promise<{
    error: AuthError | null;
  }> {
    const response = await this.client.auth.signOut();

    if (response.error) {
      // TODO: handle error
      console.error(response.error);
      throw (response.error);
    }

    return response;
  }

  public async getStravaAthlete(): Promise<StravaAthlete | null> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response: PostgrestSingleResponse<StravaAthlete | null> = await this
      .client
      .from("strava_athletes")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (response.error) {
      // TODO: handle error
      console.error("Error getting strava athlete:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async getUserProfile(): Promise<UserProfile | null> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response: PostgrestSingleResponse<UserProfile | null> = await this
      .client
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (response.error) {
      // TODO: handle error
      console.error("Error getting user profile:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async updateUserProfile(
    userId: string,
    update: UserProfileUpdate,
  ): Promise<UserProfile> {
    const response: PostgrestSingleResponse<UserProfile> = await this.client
      .from("profiles")
      .update({
        ...update,
      })
      .eq("id", userId).select().single();

    if (response.error) {
      console.error("Error updating streak count:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async saveStravaAthlete(
    exchangeStravaTokenResponse: ExchangeStravaTokenResponse,
    scope: string,
  ): Promise<StravaAthlete> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response: PostgrestSingleResponse<StravaAthlete> = await this.client
      .from("strava_athletes")
      .upsert(
        {
          id: userId,
          athlete_id: exchangeStravaTokenResponse.athlete.id,
          access_token: exchangeStravaTokenResponse.access_token,
          refresh_token: exchangeStravaTokenResponse.refresh_token,
          expires_at: new Date(exchangeStravaTokenResponse.expires_at * 1000),
          scope: scope,
          authorized: true,
        },
      ).select().single();

    if (response.error) {
      // TODO: handle error
      console.error("Error saving strava athlete:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async getStreaks(): Promise<Streak[] | null> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response: PostgrestSingleResponse<Streak[] | null> = await this
      .client
      .from("streaks")
      .select("*")
      .eq("user_id", userId);

    if (response.error) {
      // TODO: handle error
      console.error("Error getting streaks:", response.error);
      throw (response.error);
    }

    return this.streakService.sortAndUpdateStreaks(response.data);
  }

  public async getStreak(
    streakId: string,
  ): Promise<Streak | null> {
    const response: PostgrestSingleResponse<
      Streak | null
    > = await this.client
      .from("streaks")
      .select("*")
      .eq("id", streakId)
      .single();

    if (response.error) {
      console.error(
        "Error getting streak for streak id:",
        response.error,
      );
      throw (response.error);
    }

    return response.data;
  }

  public async getStravaActivities(
    streakId: string,
    daysAgo: number,
  ): Promise<StravaActivity[] | null> {
    const response: PostgrestSingleResponse<
      StreakWithActivities | null
    > = await this.client
      .from("streaks")
      .select(`
        *,
        strava_activities ( * )
      `)
      .eq("id", streakId)
      .gte(
        "strava_activities.start_date",
        this.dateHelperService.getDaysAgo(daysAgo).toISOString(),
      )
      .order("created_at", {
        referencedTable: "strava_activities",
        ascending: true,
      })
      .maybeSingle();

    if (response.error) {
      console.error(
        "Error getting Strava activities for streak id:",
        response.error,
      );
      throw (response.error);
    }

    return response.data?.strava_activities ?? null;
  }

  public async getStreakEvents(
    streakId: string,
    daysAgo: number,
  ): Promise<StreakEvent[] | null> {
    const response: PostgrestSingleResponse<StreakEvent[] | null> = await this
      .client
      .from("streak_events")
      .select("*")
      .eq("streak_id", streakId)
      .gte(
        "timestamp",
        this.dateHelperService.getDaysAgo(daysAgo).toISOString(),
      )
      .order("timestamp", {
        ascending: false,
      });

    if (response.error) {
      console.error(
        "Error getting streak events for streak id:",
        response.error,
      );
      throw (response.error);
    }

    return response.data;
  }

  public async createStreak(
    streak: StreakToCreate,
  ): Promise<Streak | null> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response: PostgrestSingleResponse<Streak | null> = await this
      .client
      .from("streaks")
      .insert(
        {
          user_id: userId,
          ...streak,
        },
      ).select().single();

    if (response.error) {
      console.error("Error creating streak:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async createStreakEvent(
    event: StreakEventToCreate,
  ): Promise<StreakEvent | null> {
    const response: PostgrestSingleResponse<StreakEvent | null> = await this
      .client
      .from("streak_events")
      .insert(
        {
          ...event,
        },
      ).select().single();

    if (response.error) {
      console.error("Error creating streak event:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async updateStreak(
    streakId: string,
    update: StreakUpdate,
  ): Promise<Streak> {
    const response: PostgrestSingleResponse<Streak> = await this.client
      .from("streaks")
      .update({
        ...update,
      })
      .eq("id", streakId).select().single();

    if (response.error) {
      console.error("Error updating streak count:", response.error);
      throw (response.error);
    }

    return response.data;
  }

  public async deleteStreak(
    id: string,
  ): Promise<PostgrestSingleResponse<null>> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response = await this
      .client
      .from("streaks")
      .delete()
      .eq(
        "id",
        id,
      );

    if (response.error) {
      // TODO: handle error
      console.error("Error deleting streak:", response.error);
      throw (response.error);
    }

    return response;
  }

  public async getLeaderboardWithUserRank(
    frequency: Frequency,
    limit: number,
  ): Promise<LeaderboardResponse> {
    const userId = this.session()?.user.id;

    if (!userId) {
      throw new Error("User is not authenticated");
    }

    const response = await this.client
      .rpc<
        "get_leaderboard_with_user_rank",
        Database["public"]["Functions"]["get_leaderboard_with_user_rank"][
          "Args"
        ]
      >("get_leaderboard_with_user_rank", {
        p_user_id: userId,
        p_frequency: frequency,
        p_limit: limit,
      });

    if (response.error) {
      console.error("Error getting leaderboard:", response.error);
      throw response.error;
    }

    return response.data;
  }
}
